import { filter, makeSubject, map, pipe, share, subscribe, tap } from 'wonka';
import { AdvertPayload } from './@types/ad-payload';
import {
  DEBUG_INFO,
  DEBUG_VERBOSE,
  getEnvironment,
  getOptions,
} from './environment';
import { beacon, makeLogUrl, infoLog } from './io';
import { omit } from './utils/object';
import { getPageDomain, getPageUrl } from './utils/page';

enum LOG_TYPE {
  log,
  info,
  debug,
  error,
}

interface Log {
  type: LOG_TYPE;
  message?: unknown;
}

interface LogValue extends Log {
  tagId: string;
  impressionId: string;
  campaignId?: string;
  reason: string;
  error?: unknown;
  celtraPlacementId?: string;
  tagd?: number;
  detail?: unknown;
}

const loggerSubject = makeSubject<Log | LogValue>();

const logger$ = pipe(loggerSubject.source, share);

/**
 * Does what it says on the tin, maps logs to the console
 */
const consoleLogger = pipe(
  logger$,
  filter(
    ({ type }) =>
      type === LOG_TYPE.debug &&
      [DEBUG_INFO, DEBUG_VERBOSE].includes(getOptions().debug),
  ),
  map(({ message }) => `[MOBKOI] ${message}`),
  subscribe(infoLog),
);

/**
 * Tag Service logger, logs back to TS/Cloudwatch
 * and attempts to put in sane values consistently for
 * every log
 */
const tagServiceLog = pipe(
  logger$,
  filter((log) => log.type === LOG_TYPE.log),
  map(
    (
      logValue,
    ): [
      ReturnType<typeof getOptions>,
      ReturnType<typeof getEnvironment>,
      Omit<LogValue, 'type'>,
    ] => [getOptions(), getEnvironment(), omit(['type'], logValue as LogValue)],
  ),
  subscribe(([{ endpoint, version }, { domain, url }, { tagId, ...rest }]) => {
    beacon(
      makeLogUrl({
        tagId,
        endpoint,
        params: {
          v: version,
          domain,
          url,
          ...rest,
        },
      }),
    );
  }),
);

const errorLogger = pipe(
  logger$,
  filter((log) => log.type === LOG_TYPE.error),
  subscribe((log) => {
    const { tagId, ...rest } = log as LogValue;
    const { endpoint, version } = getOptions();
    beacon(
      makeLogUrl({
        tagId,
        endpoint,
        params: {
          v: version,
          domain: getPageDomain(),
          url: getPageUrl(),
          ...rest,
        },
      }),
    );
  }),
);

export const log = (logValue: Omit<LogValue, 'type'>) =>
  loggerSubject.next({ type: LOG_TYPE.log, ...logValue });

export const logError = (logValue: Omit<LogValue, 'type'>) =>
  loggerSubject.next({ type: LOG_TYPE.error, ...logValue });

export const logInfo = (message: unknown) => {
  loggerSubject.next({ type: LOG_TYPE.debug, message });
};

export const debugLog = (debug: AdvertPayload['debug']) => {
  if (!debug) {
    return;
  }

  if (debug.user) {
    loggerSubject.next({
      type: LOG_TYPE.debug,
      message: `[USER] ${JSON.stringify(debug.user)}`,
    });
  }
  debug.events.forEach((event) => {
    loggerSubject.next({ type: LOG_TYPE.debug, message: event.message });
  });
};
